import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { Newspaper, RefreshCw, ArrowUp, ArrowDown, Zap, ExternalLink } from "lucide-react";
import { fetchTopHeadlines } from "@/lib/api";

const SEEN_KEY = "market_movers_seen_v1";
const FLASH_CSS = `
@keyframes hlflash {
  0%, 100% { background-color: rgba(245,158,11,0.03); }
  50% { background-color: rgba(245,158,11,0.18); }
}
.hl-flash { animation: hlflash 1.6s ease-in-out 3; }
`;

const readSeen = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

const MKT_COLOR = {
  USD: "text-amber-term border-amber-500/40",
  EUR: "text-sky-400 border-sky-500/40",
  GBP: "text-violet-400 border-violet-500/40",
  JPY: "text-rose-400 border-rose-500/40",
  AUD: "text-lime-400 border-lime-500/40",
  NZD: "text-teal-400 border-teal-500/40",
  GOLD: "text-yellow-400 border-yellow-500/40",
  OIL: "text-orange-400 border-orange-500/40",
  RATES: "text-emerald-400 border-emerald-500/40",
};

// Publisher accent colors (inline hex → identical in light & dark, immune to theme overrides).
const SRC_HEX = {
  Reuters: "#f97316",       // orange
  Bloomberg: "#6366f1",     // indigo
  AP: "#ef4444",            // red
  FinancialJuice: "#10b981",// emerald
  Investing: "#3b82f6",     // blue
  ForexLive: "#06b6d4",     // cyan
  CNBC: "#d946ef",          // fuchsia
  MarketWatch: "#84cc16",   // lime
  FT: "#ec4899",            // pink
};

function Impact({ v }) {
  if (v === "up") return <ArrowUp size={11} strokeWidth={2.5} className="text-pos shrink-0" />;
  if (v === "down") return <ArrowDown size={11} strokeWidth={2.5} className="text-neg shrink-0" />;
  return <Zap size={11} strokeWidth={2} className="text-amber-term shrink-0" />;
}

export default function TopHeadlinesCard() {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const q = useQuery({
    queryKey: ["top-headlines"],
    queryFn: () => fetchTopHeadlines(false),
    staleTime: 1000 * 60 * 20,
    refetchOnWindowFocus: false,
  });
  const d = q.data;

  const refresh = async () => {
    setRefreshing(true);
    try {
      qc.setQueryData(["top-headlines"], await fetchTopHeadlines(true));
    } catch (e) {
      /* keep prior */
    } finally {
      setRefreshing(false);
    }
  };
  const busy = q.isFetching || refreshing;

  // Flag headlines not seen since last visit; clear the flash after a few seconds.
  const [newLinks, setNewLinks] = useState(new Set());
  const timer = useRef(null);
  useEffect(() => {
    const items = d?.headlines;
    if (!items?.length) return;
    const seen = readSeen();
    const links = items.map((h) => h.link).filter(Boolean);
    const fresh = new Set(links.filter((l) => !seen.has(l)));
    setNewLinks(fresh);
    if (timer.current) clearTimeout(timer.current);
    if (fresh.size > 0) {
      timer.current = setTimeout(() => {
        try {
          const merged = [...seen, ...links].slice(-60);
          localStorage.setItem(SEEN_KEY, JSON.stringify(merged));
        } catch {
          /* ignore */
        }
        setNewLinks(new Set());
      }, 9000);
    } else {
      try {
        localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, ...links].slice(-60)));
      } catch {
        /* ignore */
      }
    }
    return () => timer.current && clearTimeout(timer.current);
  }, [d?.headlines]);

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="top-headlines-card">
      <style>{FLASH_CSS}</style>
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
          <Newspaper size={11} strokeWidth={1.5} className="text-amber-term" /> Market Movers · Live Wire
        </span>
        <button
          onClick={refresh}
          disabled={busy}
          data-testid="top-headlines-refresh"
          className="text-zinc-500 hover:text-amber-term transition-colors disabled:opacity-50"
        >
          <RefreshCw size={11} strokeWidth={1.5} className={busy ? "animate-spin" : ""} />
        </button>
      </div>

      {q.isLoading ? (
        <div className="h-[200px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">
          Scanning wires…
        </div>
      ) : q.isError || !d?.headlines?.length ? (
        <div className="h-[200px] flex items-center justify-center text-neg text-[11px] uppercase tracking-widest">
          Headlines unavailable
        </div>
      ) : (
        <ul className="divide-y divide-zinc-900">
          {d.headlines.map((h, i) => {
            const isNew = h.link && newLinks.has(h.link);
            return (
            <li
              key={h.link || i}
              className={`px-3 py-2 transition-colors border-l-2 ${isNew ? "hl-flash border-amber-500/70" : "border-transparent hover:bg-zinc-900/40"}`}
              data-testid={`top-headline-${i}`}
            >
              <a href={h.link} target="_blank" rel="noopener noreferrer" className="group block">
                {/* Row 1 · top meta: publisher + NEW on the left, impact icon far right */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {h.source && (
                      <span
                        className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest font-semibold text-zinc-400 shrink-0"
                        data-testid={`top-headline-source-${i}`}
                      >
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: SRC_HEX[h.source] || "#a1a1aa" }}
                        />
                        {h.source}
                      </span>
                    )}
                    {isNew && (
                      <span className="text-[8px] uppercase tracking-widest text-amber-term border border-amber-500/40 bg-amber-500/10 px-1 py-0.5 leading-none shrink-0" data-testid={`top-headline-new-${i}`}>
                        New
                      </span>
                    )}
                  </div>
                  <Impact v={h.impact} />
                </div>

                {/* Row 2 · headline */}
                <div className="flex items-start justify-between gap-2 mt-1.5">
                  <span className="text-[11px] leading-snug text-zinc-200 group-hover:text-amber-term transition-colors line-clamp-3">
                    {h.title}
                  </span>
                  <ExternalLink size={11} strokeWidth={1.5} className="mt-0.5 shrink-0 text-zinc-600 opacity-0 group-hover:opacity-100 group-hover:text-amber-term transition-opacity" />
                </div>

                {/* Row 3 · bottom meta: market chips + why-it-moves note */}
                {(h.markets?.length > 0 || h.note) && (
                  <div className="flex flex-col gap-1 mt-2">
                    {h.markets?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {h.markets.map((m) => (
                          <span key={m} className={`text-[9px] uppercase tracking-wider border px-1 py-0 leading-[1.5] ${MKT_COLOR[m] || "text-zinc-400 border-zinc-700"}`}>
                            {m}
                          </span>
                        ))}
                      </div>
                    )}
                    {h.note && <span className="text-[10px] text-zinc-500 leading-tight">{h.note}</span>}
                  </div>
                )}
              </a>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
